import {
  Box,
  Button,
  CustomIcon,
  DropdownMenu,
  type DropdownMenuItem,
  type DropdownMenuItemOnAction,
  Spinner,
} from '@timelyapp/react';
import { Cog, List, ShortList } from 'components/Icons';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getHourNoteGenerationState } from 'redux/selectors/hours.draft';
import timelyAnalytics from 'tracking/timely-analytics';
import type { AutoNoteStrategy } from 'types/hour';
import type { ThunkDispatch } from 'types/redux';
import { requestHourNoteGeneration } from '~domains/ai/predictions/redux/predictions.note.actions';
import styles from './AutofillBar.module.scss';
import { GeneratedNotePreview } from './GeneratedNotePreview';

interface Props {
  currentStrategy?: AutoNoteStrategy;
  defaultStrategy?: AutoNoteStrategy;
  disableAutofill?: boolean;
  tooltip?: string;
  hasEnabledTic?: boolean;
  timestampsHaveMemories?: boolean;
  onChange: (strategy: AutoNoteStrategy, setAsDefault?: boolean) => unknown;
}

const OldAutofillBar: React.FC<Props> = ({ currentStrategy, defaultStrategy, onChange }) => {
  const handleDropdownAction = useCallback<DropdownMenuItemOnAction>(
    param => {
      const action = 'action' in param.item ? param.item.action : null;
      switch (action) {
        case 'toggleAutoNote':
          onChange(param.checked ? 'memory-titles' : 'off', true);
          break;
        default:
          break;
      }
    },
    [onChange]
  );

  return (
    <div className={styles.container}>
      {currentStrategy !== 'memory-titles' && (
        <Button
          priority="low"
          size="small"
          tooltip="Autofill note based on memories"
          className={styles.OLD_TO_BE_REMOVED_button}
          onClick={() => onChange('memory-titles')}
          tabIndex={-1}
        >
          Autofill
        </Button>
      )}

      <DropdownMenu
        width={330}
        onAction={handleDropdownAction}
        closeOnSelect
        trigger={
          <Button
            size="small"
            priority="low"
            className={styles.OLD_TO_BE_REMOVED_cog}
            tabIndex={-1}
          >
            <Cog />
          </Button>
        }
        items={[
          {
            action: 'toggleAutoNote',
            label: 'Always autofill note based on memories',
            type: 'switch',
            checked: defaultStrategy === 'memory-titles',
          },
        ]}
      />
    </div>
  );
};

const AutofillBar: React.FC<Props> = ({
  currentStrategy,
  defaultStrategy,
  onChange,
  disableAutofill,
  tooltip,
  hasEnabledTic,
  timestampsHaveMemories,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { generatedNotes, shouldRegenerateNote, isGeneratingNote } = useSelector(
    getHourNoteGenerationState
  );
  const dispatch = useDispatch<ThunkDispatch>();

  const handleDropdownAction = useCallback<DropdownMenuItemOnAction>(
    param => {
      const action = 'action' in param.item ? param.item.action : null;
      if (action === 'toggleClassicAutofill') {
        onChange(param.checked ? 'memory-titles' : 'off', true);
        return;
      }
      if (action === 'useClassicAutofill') {
        onChange('memory-titles');
        return;
      }
      if (!action) {
        return;
      }
      onChange(action as AutoNoteStrategy);
      setIsOpen(false);
      timelyAnalytics.trackTic('TIC+AI Click TA Autofill Note', {
        mode: action ?? 'unknown',
      });
    },
    [onChange]
  );

  /**
   * @todo This whole menu should not be a menu!
   * @todo Convert to just Dropdown, but until then, move things into 1 custom..
   */
  const options: DropdownMenuItem[] = React.useMemo(() => {
    const customElementContent = (
      <Box.Column gap="medium" width="100%" className={styles.customElement}>
        <GeneratedNotePreview
          title={'Concise'}
          icon={<ShortList />}
          note={generatedNotes?.without_reference}
          expectedLength="short"
          onSelect={() =>
            handleDropdownAction({
              item: {
                type: 'item',
                action: 'without_reference',
                // just to make typescript happy
                label: '',
              },
            })
          }
        />
        <GeneratedNotePreview
          title={'Detailed'}
          icon={<List />}
          note={generatedNotes?.with_reference}
          expectedLength="long"
          onSelect={() =>
            handleDropdownAction({
              item: {
                type: 'item',
                action: 'with_reference',
                // just to make typescript happy
                label: '',
              },
            })
          }
        />
      </Box.Column>
    );

    const items: DropdownMenuItem[] = [
      {
        type: 'label',
        label: 'Autofill note generated by AI',
        indent: false,
      },
      {
        type: 'custom',
        content: customElementContent,
      },
      {
        type: 'separator',
      },
    ];

    items.push({
      type: 'switch',
      checked: defaultStrategy === 'memory-titles',
      label: `Fallback to autofill using memories`,
      action: 'toggleClassicAutofill',
    });
    const hasGeneratedNotes =
      !!generatedNotes?.with_reference || !!generatedNotes?.without_reference;

    if (!timestampsHaveMemories && !hasGeneratedNotes) {
      items.push(
        { type: 'separator' },
        {
          type: 'message',
          variant: hasGeneratedNotes ? 'info' : 'alert',
          label: 'Autofill missing memories',
          description: 'Select memories to begin autofilling notes',
          icon: 'auto',
        }
      );
    }

    return items;
  }, [
    defaultStrategy,
    generatedNotes?.with_reference,
    generatedNotes?.without_reference,
    handleDropdownAction,
    timestampsHaveMemories,
  ]);

  const domId = 'ai-magic-button';

  if (!hasEnabledTic) {
    return (
      <OldAutofillBar
        currentStrategy={currentStrategy}
        defaultStrategy={defaultStrategy}
        onChange={onChange}
        disableAutofill={disableAutofill}
      />
    );
  }

  return (
    <>
      <DropdownMenu
        width={330}
        onAction={handleDropdownAction}
        onOpenChange={isOpen => {
          if (isOpen && shouldRegenerateNote && !isGeneratingNote) {
            dispatch(requestHourNoteGeneration());
          }
          setIsOpen(isOpen);
        }}
        contentClassName={styles.dropdownContent}
        side="bottom"
        align="start"
        items={options}
        disabled={disableAutofill}
        open={isOpen}
        trigger={
          <Button
            id={domId}
            data-testid={domId}
            priority="low"
            variant={'success'}
            size="small"
            tooltip={isGeneratingNote ? 'Generating note...' : tooltip}
            disabled={disableAutofill}
            onClick={() => setIsOpen(!isOpen)}
          >
            <div style={{ width: 14, height: 14 }}>
              {isGeneratingNote ? (
                <Spinner size={14} />
              ) : (
                <CustomIcon.Sparkling width={14} height={14} />
              )}
            </div>
            Autofill
          </Button>
        }
      />
    </>
  );
};

export default React.memo(AutofillBar);
